import { useRef } from "react";
import { ListFilterIcon, CircleXIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCreatorsTableContext, useCreatorsTableState } from "../CreatorsTableContext";
import { SearchFilterProps } from "../types";

export default function SearchFilter({ id }: SearchFilterProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  // The table mirrors the text into ?query= itself (debounced) — nothing
  // here touches the router, so a keystroke is one local state update.
  const { setGlobalFilter } = useCreatorsTableContext();
  const { globalFilter } = useCreatorsTableState();

  // flex-1 capped at the old fixed width: full 320px when there's room,
  // shrinks toward min-w-40 when the thread panel narrows the table.
  return (
    <div className="relative h-full min-w-40 max-w-80 flex-1">
      <Input
        id={`${id}-input`}
        ref={inputRef}
        className={cn(
          // Inbox-style segment: borderless, fills its toolbar slot.
          "peer h-full w-full rounded-none border-none bg-transparent ps-8 placeholder:text-xs text-xs shadow-none focus-visible:ring-0",
          Boolean(globalFilter) && "pe-8"
        )}
        value={globalFilter ?? ""}
        onChange={(e) => setGlobalFilter(e.target.value)}
        placeholder="Filter by username or email..."
        type="text"
        aria-label="Filter by username or email"
      />

      <div className="text-muted-foreground/80 pointer-events-none absolute inset-y-0 inset-s-0 flex items-center justify-center ps-2 peer-disabled:opacity-50">
        <ListFilterIcon size={16} aria-hidden="true" />
      </div>
      {Boolean(globalFilter) && (
        <Button
          className="absolute inset-y-0 my-auto inset-e-1 text-muted-foreground/80 hover:text-foreground hover:bg-transparent"
          aria-label="Clear filter"
          variant="ghost"
          size="icon"
          scope={false}
          onClick={() => {
            setGlobalFilter("");
            if (inputRef.current) {
              inputRef.current.focus();
            }
          }}
        >
          <CircleXIcon aria-hidden="true" />
        </Button>
      )}
    </div>
  );
}
